import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { EllipsisVerticalIcon } from "@heroicons/react/24/outline";
import { useTranslation } from "react-i18next";

/**
 * Kebab menu for HSE list rows. Stops row-click, portals so overflow cannot clip it.
 */
export function HseRecordActionsMenu({ items = [], label }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const buttonRef = useRef(null);
  const visible = items.filter((item) => item && item.hidden !== true);

  const place = useCallback(() => {
    const btn = buttonRef.current;
    if (!btn) return;
    const rect = btn.getBoundingClientRect();
    const width = 220;
    const left = Math.max(8, Math.min(rect.right - width, window.innerWidth - width - 8));
    setPos({ top: rect.bottom + 8, left });
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    place();
    const onScroll = () => place();
    const onKey = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onScroll);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onScroll);
      window.removeEventListener("keydown", onKey);
    };
  }, [open, place]);

  if (visible.length === 0) return null;

  return (
    <div
      className="inline-flex"
      onClick={(event) => event.stopPropagation()}
      onKeyDown={(event) => event.stopPropagation()}
    >
      <button
        ref={buttonRef}
        type="button"
        className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={label || t("hse.actions.menu")}
        onClick={(event) => {
          event.stopPropagation();
          setOpen((value) => !value);
        }}
      >
        <EllipsisVerticalIcon className="h-5 w-5" aria-hidden />
      </button>
      {open &&
        typeof document !== "undefined" &&
        createPortal(
          <Fragment>
            <div
              className="fixed inset-0 z-[80]"
              onClick={() => setOpen(false)}
            />
            <div
              role="menu"
              className="fixed z-[81] w-[220px] rounded-[11px] border border-[#dfe5ec] bg-white p-1.5 shadow-[0_12px_32px_rgba(20,35,55,.14)]"
              style={{ top: pos.top, left: pos.left }}
            >
              {visible.map((item) => {
                const Icon = item.icon;
                const danger = item.danger === true;
                return (
                  <button
                    key={item.key || item.label}
                    type="button"
                    role="menuitem"
                    disabled={item.disabled}
                    className={`flex w-full items-center gap-2 rounded-lg px-3 py-2.5 text-left text-sm disabled:cursor-not-allowed disabled:opacity-50 ${
                      danger
                        ? "text-rose-700 hover:bg-rose-50"
                        : "text-[#33445a] hover:bg-[#f2f5f9]"
                    }`}
                    onClick={() => {
                      if (item.disabled) return;
                      setOpen(false);
                      item.onClick?.();
                    }}
                  >
                    {Icon ? (
                      <Icon
                        className={`h-5 w-5 ${danger ? "text-rose-500" : "text-slate-400"}`}
                        aria-hidden
                      />
                    ) : null}
                    {item.label}
                  </button>
                );
              })}
            </div>
          </Fragment>,
          document.body
        )}
    </div>
  );
}

export default HseRecordActionsMenu;
